/**
 * SignatureQueuePanel — /admin tab for managing Signature Experiences
 * image hierarchy and publish gate.
 *
 * Founder ruling 16 Feb 2026 PM-16: build the admin queue · the desk
 * needs to upload / approve / flag / replace images and trigger the
 * publish gate from one screen.
 *
 * Surface:
 *   · grid of all Signature drafts with thumbnail · title · status
 *   · filters: image_status (all / ready / pending_review / flagged /
 *     missing), publish_status, desk_review_flag, search
 *   · per-row actions:
 *       - upload photo (Cloudinary-backed · survives deployment)
 *       - approve current image
 *       - flag image (wrong-building / poor / mismatch)
 *       - set placeholder (sports/arts/dining/festival/motorsport/destination)
 *       - "why this photo?" desk peek
 *   · bulk actions: audit-and-quarantine sweep
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiError } from "@/lib/api";

const STATUS_FILTERS = [
  { k: "", l: "All" },
  { k: "ready", l: "Ready (live)" },
  { k: "pending_review", l: "Pending review" },
  { k: "flagged", l: "Flagged" },
  { k: "missing", l: "Missing image" },
];

const PLACEHOLDER_KINDS = [
  { k: "sports", l: "Sports · laurel arc" },
  { k: "arts", l: "Arts · theatre curtain" },
  { k: "dining", l: "Dining · fluted column" },
  { k: "festival", l: "Festival · starburst" },
  { k: "motorsport", l: "Motorsport · checkered chevron" },
  { k: "destination", l: "Destination · horizon" },
];

const STATUS_STYLES = {
  ready: { bg: "rgba(15, 92, 31, 0.15)", color: "#0f5c1f", label: "READY" },
  pending_review: { bg: "rgba(160, 122, 56, 0.2)", color: "#6E5326", label: "PENDING" },
  flagged: { bg: "rgba(176, 50, 32, 0.18)", color: "#7a1f10", label: "FLAGGED" },
  missing: { bg: "rgba(120, 120, 120, 0.15)", color: "#555", label: "MISSING" },
};


function StatusBadge({ status }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.missing;
  return (
    <span
      data-testid={`sig-queue-row-status-${status}`}
      className="t-mono uppercase"
      style={{
        fontSize: 9, letterSpacing: "0.16em",
        padding: "3px 8px", borderRadius: 100,
        background: style.bg, color: style.color,
        fontWeight: 600,
      }}
    >{style.label}</span>
  );
}


function ImageAuditPeek({ draftId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get(`/admin/signature/${draftId}/image-audit`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setErr(formatApiError(e.response?.data?.detail) || e.message); });
    return () => { cancelled = true; };
  }, [draftId]);

  return (
    <div
      data-testid="sig-queue-audit-peek"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(33,28,21,0.55)", zIndex: 60,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#FAF7EE",
          border: "1px solid #E7DFC9",
          borderRadius: 6, padding: "24px 28px",
          maxWidth: 600, width: "100%",
          fontFamily: "'IBM Plex Mono', monospace",
        }}
      >
        <p className="t-mono uppercase" style={{ fontSize: 10, letterSpacing: "0.22em", color: "#6E5326", fontWeight: 600, marginBottom: 16 }}>
          Why this photo?
        </p>
        {err && <p style={{ color: "#7a1f10", fontSize: 13 }}>{err}</p>}
        {!err && !data && <p style={{ fontSize: 13, color: "#555" }}>Loading audit…</p>}
        {data && (
          <dl style={{ display: "grid", gridTemplateColumns: "150px 1fr", rowGap: 8, fontSize: 13, color: "#2A2520" }}>
            <dt style={{ color: "#555" }}>Title</dt>
            <dd style={{ margin: 0 }}>{data.title}</dd>
            <dt style={{ color: "#555" }}>City</dt>
            <dd style={{ margin: 0 }}>{data.city || "—"}</dd>
            <dt style={{ color: "#555" }}>Places match</dt>
            <dd style={{ margin: 0 }}>{data.places_name || "—"}</dd>
            <dt style={{ color: "#555" }}>Places address</dt>
            <dd style={{ margin: 0 }}>{data.places_address || "—"}</dd>
            <dt style={{ color: "#555" }}>Source type</dt>
            <dd style={{ margin: 0 }}>{data.hero_source_type || "none"}</dd>
            <dt style={{ color: "#555" }}>Placeholder kind</dt>
            <dd style={{ margin: 0 }}>{data.placeholder_kind || "—"}</dd>
            <dt style={{ color: "#555" }}>Image status</dt>
            <dd style={{ margin: 0 }}>{data.image_status} {data.image_status_reason ? `· ${data.image_status_reason}` : ""}</dd>
            {data.photo_attribution_html && (
              <>
                <dt style={{ color: "#555" }}>Credit</dt>
                <dd style={{ margin: 0 }} dangerouslySetInnerHTML={{ __html: data.photo_attribution_html }} />
              </>
            )}
          </dl>
        )}
        <button
          onClick={onClose}
          data-testid="sig-queue-audit-close"
          style={{
            marginTop: 20, padding: "8px 16px",
            background: "transparent", border: "1px solid #E7DFC9",
            borderRadius: 4, fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase",
            cursor: "pointer", color: "#2A2520",
          }}
        >Close</button>
      </div>
    </div>
  );
}


function RowActions({ row, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const fileRef = useRef(null);

  const call = async (fn) => {
    setBusy(true);
    try { await fn(); onChanged && onChanged(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const onUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/admin/signature/${row.id}/upload-image`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChanged && onChanged();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
      event.target.value = "";
    }
  };

  const isPublished = row.publish_status === "published";

  return (
    <div data-testid={`sig-queue-row-actions-${row.id}`} style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
      <input
        type="file" ref={fileRef} accept="image/*" style={{ display: "none" }}
        onChange={onUpload}
        data-testid={`sig-queue-upload-input-${row.id}`}
      />
      <ActionButton
        testid={`sig-queue-upload-${row.id}`}
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        label="Upload"
        primary
      />
      <ActionButton
        testid={`sig-queue-edit-${row.id}`}
        onClick={() => setShowEdit(true)}
        disabled={busy}
        label="Edit"
      />
      {isPublished ? (
        <ActionButton
          testid={`sig-queue-deactivate-${row.id}`}
          onClick={() => {
            if (confirm(`Deactivate "${row.title}" — take it off the member site?`)) {
              call(() => api.post(`/admin/signature/${row.id}/deactivate`));
            }
          }}
          disabled={busy}
          label="Deactivate"
        />
      ) : (
        <ActionButton
          testid={`sig-queue-activate-${row.id}`}
          onClick={() => call(() => api.post(`/admin/signature/${row.id}/activate`))}
          disabled={busy || row.image_status !== "ready"}
          label="Activate"
        />
      )}
      <ActionButton
        testid={`sig-queue-flag-${row.id}`}
        onClick={() => {
          const reason = prompt("Reason · wrong_building / wrong_event / poor_quality / other", "wrong_building");
          if (reason) call(() => api.post(`/admin/signature/${row.id}/flag-image?reason=${encodeURIComponent(reason)}`));
        }}
        disabled={busy}
        label="Flag"
      />
      <div style={{ position: "relative" }}>
        <ActionButton
          testid={`sig-queue-placeholder-${row.id}`}
          onClick={() => setShowPicker(!showPicker)}
          disabled={busy}
          label="Placeholder ▾"
        />
        {showPicker && (
          <div style={{
            position: "absolute", top: "calc(100% + 4px)", right: 0,
            background: "var(--paper, #FAF7EE)",
            border: "1px solid #E7DFC9", borderRadius: 4,
            padding: 6, minWidth: 220, zIndex: 5,
            boxShadow: "0 4px 12px rgba(33,28,21,0.18)",
          }}>
            {PLACEHOLDER_KINDS.map((p) => (
              <button
                key={p.k}
                data-testid={`sig-queue-placeholder-pick-${row.id}-${p.k}`}
                onClick={() => {
                  setShowPicker(false);
                  call(() => api.post(`/admin/signature/${row.id}/set-placeholder?kind=${p.k}`));
                }}
                style={{
                  display: "block", width: "100%", textAlign: "left",
                  padding: "6px 10px", border: 0, background: "transparent",
                  cursor: "pointer", fontSize: 11,
                  fontFamily: "'IBM Plex Mono', monospace",
                  color: "#2A2520",
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = "#F4EBD7"}
                onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
              >{p.l}</button>
            ))}
          </div>
        )}
      </div>
      <ActionButton
        testid={`sig-queue-audit-${row.id}`}
        onClick={() => setShowAudit(true)}
        disabled={busy}
        label="Why?"
      />
      <ActionButton
        testid={`sig-queue-delete-${row.id}`}
        onClick={() => {
          if (confirm(`Delete "${row.title}" PERMANENTLY · this cannot be undone. Continue?`)) {
            call(() => api.delete(`/admin/signature/${row.id}`));
          }
        }}
        disabled={busy}
        label="Delete"
        danger
      />
      {showAudit && <ImageAuditPeek draftId={row.id} onClose={() => setShowAudit(false)} />}
      {showEdit && (
        <EditSignatureModal
          row={row}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); onChanged && onChanged(); }}
        />
      )}
    </div>
  );
}


function ActionButton({ testid, onClick, disabled, label, primary = false, danger = false }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className="t-mono uppercase"
      style={{
        fontSize: 10, letterSpacing: "0.14em",
        padding: "5px 10px", borderRadius: 4,
        border: danger ? "1px solid #b03220" : "1px solid #E7DFC9",
        background: primary ? "#8B6914" : (danger ? "transparent" : "transparent"),
        color: primary ? "#fff" : (danger ? "#7a1f10" : "#2A2520"),
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        fontWeight: 600,
      }}
    >{label}</button>
  );
}


// ── Add/Edit Signature modal · founder ruling 16 Feb 2026 PM-17 ──
function EditSignatureModal({ row, onClose, onSaved }) {
  const isNew = !row;
  const [form, setForm] = useState({
    title: row?.title || "",
    category: row?.category || "sports",
    city: row?.city || "",
    country: row?.country || "",
    venue_name: row?.venue_name || "",
    dates: row?.dates || "",
    access_type: row?.access_type || "Curated",
    concierge_whisper: row?.concierge_whisper || "",
    best_for: row?.best_for || "",
    member_facing_notes: row?.member_facing_notes || "",
    indicative_price: row?.indicative_price || "On request",
    priority: row?.priority ?? 50,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  // Founder ruling 16 Feb 2026 PM-24 · image upload INSIDE the edit modal
  // so the desk can change a hero image without leaving the edit flow.
  const [heroUrl, setHeroUrl] = useState(row?.hero_image_url || "");
  const [imgStatus, setImgStatus] = useState(row?.image_status || "missing");
  const [imgBusy, setImgBusy] = useState(false);
  const [imgErr, setImgErr] = useState("");
  const fileRef = useRef(null);

  const onChangeImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !row?.id) return;
    setImgBusy(true); setImgErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(
        `/admin/signature/${row.id}/upload-image`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      // Backend returns { image_url, image_status, ... }.
      // For desk-curated uploads, image_url == hero_image_url (the
      // hierarchy resolves desk_curated to itself). Cache-bust so the
      // browser fetches the fresh Cloudinary asset.
      const updated = r?.data || {};
      const fresh = updated.image_url || updated.hero_image_url;
      if (fresh) {
        const buster = fresh.includes("?") ? "&" : "?";
        setHeroUrl(`${fresh}${buster}v=${Date.now()}`);
      }
      setImgStatus(updated.image_status || "ready");
    } catch (e) {
      setImgErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setImgBusy(false);
      event.target.value = "";
    }
  };

  const onRemoveImage = async () => {
    if (!row?.id) return;
    if (!confirm(`Remove the current image for "${row.title}"? The card will fall back to a placeholder until a new one is uploaded.`)) {
      return;
    }
    setImgBusy(true); setImgErr("");
    try {
      await api.delete(`/admin/signature/${row.id}/image`);
      setHeroUrl("");
      setImgStatus("missing");
    } catch (e) {
      setImgErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setImgBusy(false);
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setSaving(true); setErr("");
    try {
      if (isNew) {
        await api.post("/admin/signature/create", form);
      } else {
        await api.patch(`/admin/signature/${row.id}`, form);
      }
      onSaved();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="sig-queue-edit-modal"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(33,28,21,0.55)", zIndex: 70,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        style={{
          background: "#FAF7EE",
          border: "1px solid #E7DFC9", borderRadius: 6,
          padding: "24px 28px",
          maxWidth: 640, width: "100%",
          maxHeight: "90vh", overflowY: "auto",
        }}
      >
        <p className="t-mono uppercase" style={{
          fontSize: 10, letterSpacing: "0.22em",
          color: "#6E5326", fontWeight: 600, marginBottom: 6,
        }}>
          {isNew ? "Add Signature" : "Edit Signature"}
        </p>
        <h3 className="t-serif" style={{
          fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 500,
          margin: "0 0 18px",
        }}>{isNew ? "Create a new Signature row" : form.title || "(untitled)"}</h3>

        {/* ── Image · founder ruling 16 Feb 2026 PM-24 ──────────────
              Live preview + change image + remove image from inside
              the edit flow (in addition to the per-row Upload button).
              For new rows we hide the section · need an id first. */}
        {!isNew && (
          <div
            data-testid="sig-queue-edit-image-section"
            style={{
              display: "grid", gridTemplateColumns: "120px 1fr", gap: 14,
              alignItems: "center", marginBottom: 18,
              padding: "12px 14px",
              border: "1px dashed #D9CDB7", borderRadius: 6,
              background: "#FBF6EC",
            }}
          >
            <div
              data-testid="sig-queue-edit-image-preview"
              style={{
                width: 120, height: 88, borderRadius: 4,
                overflow: "hidden", background: "#EFE7D2",
                display: "flex", alignItems: "center", justifyContent: "center",
                border: "1px solid #E7DFC9",
              }}
            >
              {heroUrl ? (
                <img
                  src={heroUrl}
                  alt={form.title || "Signature"}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : (
                <span
                  className="t-mono uppercase"
                  style={{
                    fontSize: 9, letterSpacing: "0.18em",
                    color: "#7d6f5c",
                  }}
                >No image</span>
              )}
            </div>
            <div>
              <div
                className="t-mono uppercase"
                style={{
                  fontSize: 10, letterSpacing: "0.20em",
                  color: "#6E5326", fontWeight: 600, marginBottom: 6,
                }}
              >Hero image · {imgStatus}</div>
              <p style={{
                fontSize: 12, color: "#5a5044", lineHeight: 1.45,
                margin: "0 0 8px",
              }}>
                Recommended ≥ 1200×800, ≤ 4 MB. Member-facing publish gate
                requires <code>image_status=ready</code>.
              </p>
              <input
                type="file" ref={fileRef} accept="image/*"
                style={{ display: "none" }}
                onChange={onChangeImage}
                data-testid="sig-queue-edit-image-input"
              />
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  data-testid="sig-queue-edit-image-change"
                  onClick={() => fileRef.current?.click()}
                  disabled={imgBusy}
                  className="t-mono uppercase"
                  style={{
                    fontSize: 10, letterSpacing: "0.18em",
                    padding: "6px 12px", borderRadius: 4,
                    border: "1px solid #8B6914",
                    background: heroUrl ? "transparent" : "#8B6914",
                    color: heroUrl ? "#6E5326" : "#fff",
                    cursor: imgBusy ? "wait" : "pointer", fontWeight: 600,
                  }}
                >{imgBusy ? "Uploading…" : (heroUrl ? "Change image" : "Upload image")}</button>
                {heroUrl && (
                  <button
                    type="button"
                    data-testid="sig-queue-edit-image-remove"
                    onClick={onRemoveImage}
                    disabled={imgBusy}
                    className="t-mono uppercase"
                    style={{
                      fontSize: 10, letterSpacing: "0.18em",
                      padding: "6px 12px", borderRadius: 4,
                      border: "1px solid #c95d4e",
                      background: "transparent",
                      color: "#7a1f10",
                      cursor: imgBusy ? "wait" : "pointer", fontWeight: 600,
                    }}
                  >Remove image</button>
                )}
              </div>
              {imgErr && (
                <p
                  data-testid="sig-queue-edit-image-error"
                  style={{ color: "#7a1f10", fontSize: 11, marginTop: 6 }}
                >{imgErr}</p>
              )}
            </div>
          </div>
        )}
        {isNew && (
          <p
            data-testid="sig-queue-edit-image-locked"
            className="t-mono uppercase"
            style={{
              fontSize: 10, letterSpacing: "0.18em",
              color: "#7d6f5c", fontWeight: 600,
              margin: "0 0 18px",
              padding: "10px 14px",
              border: "1px dashed #D9CDB7", borderRadius: 6,
              background: "#FBF6EC",
            }}
          >Save the row first · then return here to upload its hero image.</p>
        )}

        <div style={{ display: "grid", gap: 12 }}>
          <Field label="Title *">
            <input
              required
              data-testid="sig-queue-edit-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              style={inputStyle()}
              placeholder="Wimbledon debenture day with hospitality"
            />
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Field label="Category">
              <select
                data-testid="sig-queue-edit-category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                style={inputStyle()}
              >
                {["sports", "arts", "dining", "festival", "motorsport", "destination",
                  "Sport / tennis", "Sport / football", "Sport / motorsport",
                  "Film / theatre awards", "Film / festival", "Awards / television",
                  "Awards / music", "Fashion / style", "Music / entertainment",
                  "Gala / philanthropy / society", "Private party / social access",
                  "Marquee experience"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Access type">
              <select
                value={form.access_type}
                onChange={(e) => setForm({ ...form, access_type: e.target.value })}
                style={inputStyle()}
              >
                <option value="Curated">Curated</option>
                <option value="Non-bookable">Non-bookable</option>
                <option value="By-invitation">By-invitation</option>
                <option value="On-request">On-request</option>
              </select>
            </Field>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Field label="City">
              <input
                data-testid="sig-queue-edit-city"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                style={inputStyle()}
                placeholder="London"
              />
            </Field>
            <Field label="Country (ISO-2)">
              <input
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                style={inputStyle()}
                placeholder="GB"
                maxLength={2}
              />
            </Field>
          </div>
          <Field label="Venue name (helps Places resolver)">
            <input
              data-testid="sig-queue-edit-venue"
              value={form.venue_name}
              onChange={(e) => setForm({ ...form, venue_name: e.target.value })}
              style={inputStyle()}
              placeholder="All England Lawn Tennis Club"
            />
          </Field>
          <Field label="Dates (free text)">
            <input
              value={form.dates}
              onChange={(e) => setForm({ ...form, dates: e.target.value })}
              style={inputStyle()}
              placeholder="3-13 July 2026"
            />
          </Field>
          <Field label="Concierge® whisper (italic on the card)">
            <textarea
              data-testid="sig-queue-edit-whisper"
              value={form.concierge_whisper}
              onChange={(e) => setForm({ ...form, concierge_whisper: e.target.value })}
              rows={3}
              style={{ ...inputStyle(), resize: "vertical", fontFamily: "Fraunces, serif", fontStyle: "italic" }}
              placeholder="A debenture day on Centre Court · scones at the Members' Enclosure · port at full time."
            />
          </Field>
          <Field label="Best for">
            <input
              value={form.best_for}
              onChange={(e) => setForm({ ...form, best_for: e.target.value })}
              style={inputStyle()}
              placeholder="Members who want a full-day Wimbledon hospitality"
            />
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Field label="Indicative price">
              <input
                value={form.indicative_price}
                onChange={(e) => setForm({ ...form, indicative_price: e.target.value })}
                style={inputStyle()}
                placeholder="On request"
              />
            </Field>
            <Field label="Priority (sort)">
              <input
                type="number"
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value || "50", 10) })}
                style={inputStyle()}
                min={0} max={100}
              />
            </Field>
          </div>
        </div>

        {err && <p style={{ color: "#7a1f10", fontSize: 13, marginTop: 14 }} data-testid="sig-queue-edit-error">{err}</p>}

        <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={onClose}
            className="t-mono uppercase"
            style={{
              fontSize: 11, letterSpacing: "0.18em",
              padding: "8px 16px", borderRadius: 4,
              border: "1px solid #E7DFC9", background: "transparent",
              cursor: "pointer", color: "#2A2520",
            }}
          >Cancel</button>
          <button
            type="submit"
            data-testid="sig-queue-edit-save"
            disabled={saving || !form.title}
            className="t-mono uppercase"
            style={{
              fontSize: 11, letterSpacing: "0.18em",
              padding: "8px 16px", borderRadius: 4,
              border: "1px solid #8B6914", background: "#8B6914",
              cursor: saving ? "not-allowed" : "pointer",
              color: "#fff", fontWeight: 600,
              opacity: !form.title ? 0.4 : 1,
            }}
          >{saving ? "Saving…" : (isNew ? "Create draft" : "Save changes")}</button>
        </div>
      </form>
    </div>
  );
}


function Field({ label, children }) {
  return (
    <label style={{ display: "block" }}>
      <span className="t-mono uppercase" style={{
        fontSize: 10, letterSpacing: "0.14em",
        color: "#6E5326", fontWeight: 600, marginBottom: 4, display: "block",
      }}>{label}</span>
      {children}
    </label>
  );
}


function inputStyle() {
  return {
    width: "100%", padding: "8px 12px",
    border: "1px solid #E7DFC9", borderRadius: 4,
    fontSize: 13, color: "#2A2520",
    background: "#FFFFFF",
    fontFamily: "'IBM Plex Mono', monospace",
  };
}


export default function SignatureQueuePanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      // Pull ALL drafts (admin view) · the backend admin route returns
      // every row regardless of publish_status / image_status.
      const r = await api.get("/admin/signature/drafts", { params: { limit: 300 } });
      setRows(r.data.items || r.data || []);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const runSweep = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/signature/audit-and-quarantine-images");
      alert(`Sweep complete:\n· ready: ${r.data.ready}\n· pending review: ${r.data.pending_review}\n· missing: ${r.data.missing}\n· flagged kept: ${r.data.flagged_kept}`);
      refresh();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const filtered = rows.filter((r) => {
    if (statusFilter && (r.image_status || "missing") !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(r.title || "").toLowerCase().includes(q) &&
          !(r.city || "").toLowerCase().includes(q) &&
          !(r.category || "").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const counts = rows.reduce((acc, r) => {
    const s = r.image_status || "missing";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <div data-testid="sig-queue-panel" style={{ color: "#2A2520" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
        <div>
          <h2 className="t-serif" style={{ fontFamily: "Fraunces, serif", fontSize: 28, fontWeight: 500, margin: 0 }}>
            Signature queue
          </h2>
          <p style={{ fontSize: 13, color: "#555", marginTop: 4, maxWidth: 540 }}>
            Image hierarchy + publish gate. No member-facing card publishes without
            <code style={{ background: "#F4EBD7", padding: "1px 6px", borderRadius: 3, margin: "0 4px", fontSize: 11 }}>image_status=&quot;ready&quot;</code>.
            Upload photos to Cloudinary so they survive deployment.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
          <button
            data-testid="sig-queue-add"
            onClick={() => setShowAdd(true)}
            className="t-mono uppercase"
            style={{
              fontSize: 10, letterSpacing: "0.18em",
              padding: "8px 14px", borderRadius: 4,
              border: "1px solid #8B6914", background: "#8B6914",
              color: "#fff", cursor: "pointer", fontWeight: 600,
            }}
          >+ Add signature</button>
          <button
            data-testid="sig-queue-run-sweep"
            onClick={runSweep}
            disabled={busy}
            className="t-mono uppercase"
            style={{
              fontSize: 10, letterSpacing: "0.18em",
              padding: "8px 14px", borderRadius: 4,
              border: "1px solid #8B6914", background: "transparent",
              color: "#6E5326", cursor: "pointer", fontWeight: 600,
            }}
          >Run audit sweep</button>
        </div>
      </div>

      {/* Filter strip */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 18, flexWrap: "wrap" }}>
        {STATUS_FILTERS.map((f) => {
          const n = f.k ? (counts[f.k] || 0) : rows.length;
          const active = statusFilter === f.k;
          return (
            <button
              key={f.k || "all"}
              data-testid={`sig-queue-filter-${f.k || "all"}`}
              onClick={() => setStatusFilter(f.k)}
              className="t-mono uppercase"
              style={{
                fontSize: 10, letterSpacing: "0.14em",
                padding: "6px 12px", borderRadius: 100,
                border: active ? "1px solid #8B6914" : "1px solid #E7DFC9",
                background: active ? "#8B6914" : "transparent",
                color: active ? "#fff" : "#2A2520",
                cursor: "pointer", fontWeight: 600,
              }}
            >{f.l} ({n})</button>
          );
        })}
        <input
          data-testid="sig-queue-search"
          type="text"
          placeholder="Search title / city / category…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            marginLeft: "auto", padding: "6px 12px",
            border: "1px solid #E7DFC9", borderRadius: 4,
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
            background: "#FAF7EE", minWidth: 240,
          }}
        />
      </div>

      {err && (
        <p data-testid="sig-queue-error" style={{ color: "#7a1f10", fontSize: 13 }}>{err}</p>
      )}
      {loading && <p style={{ fontSize: 13, color: "#555" }}>Loading queue…</p>}

      {/* Grid */}
      {!loading && (
        <div data-testid="sig-queue-grid" style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: 14,
        }}>
          {filtered.map((row) => (
            <div
              key={row.id}
              data-testid={`sig-queue-row-${row.id}`}
              style={{
                background: "#FAF7EE",
                border: "1px solid #E7DFC9", borderRadius: 6,
                overflow: "hidden",
                display: "flex", flexDirection: "column",
              }}
            >
              {/* Thumb */}
              <div style={{
                aspectRatio: "16/10",
                background: "#F4EBD7",
                position: "relative",
                borderBottom: "1px solid #E7DFC9",
              }}>
                {row.hero_image_url ? (
                  <img
                    src={row.hero_image_url}
                    alt=""
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                    data-testid={`sig-queue-row-thumb-${row.id}`}
                  />
                ) : (
                  /* Admin queue ONLY: monogram fallback is OK here per founder
                     ruling 16 Feb 2026 PM-14 ("acceptable in the admin queue,
                     NEVER as a published member-facing card") */
                  <div
                    data-testid={`sig-queue-row-monogram-${row.id}`}
                    style={{
                      width: "100%", height: "100%",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontFamily: "Fraunces, serif", fontSize: 48,
                      color: "rgba(160,122,56,0.5)",
                    }}
                  >{(row.title || "·")[0]}</div>
                )}
                <div style={{ position: "absolute", top: 8, right: 8 }}>
                  <StatusBadge status={row.image_status || "missing"} />
                </div>
              </div>
              {/* Body */}
              <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
                <p className="t-serif" style={{
                  fontFamily: "Fraunces, serif", fontSize: 15,
                  lineHeight: 1.25, margin: 0, color: "#2A2520",
                }}>{row.title}</p>
                <p className="t-mono" style={{
                  fontSize: 10, letterSpacing: "0.08em", color: "#555",
                  margin: 0, textTransform: "uppercase",
                }}>
                  {[row.category, row.city, row.country].filter(Boolean).join(" · ")}
                </p>
                <p className="t-mono" style={{ fontSize: 10, color: "#6E5326", margin: 0 }}>
                  publish · {row.publish_status || "?"} · src · {row.hero_image_source_type || "none"}
                </p>
                <div style={{ marginTop: "auto", paddingTop: 8 }}>
                  <RowActions row={row} onChanged={refresh} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {!loading && filtered.length === 0 && (
        <p style={{ fontSize: 13, color: "#555", padding: 40, textAlign: "center" }}>
          No rows match the current filters.
        </p>
      )}
      {showAdd && (
        <EditSignatureModal
          row={null}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); refresh(); }}
        />
      )}
    </div>
  );
}
